// src/app/modules/commissionClient/ClientCommission.tsx
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import Select from 'react-select'
import Swal from 'sweetalert2'

import {
  fetchCustomers,
  fetchClientCommissions,
  saveClientCommissions,
} from './apis/requestsApi'
import {ClientCommissionItem} from './core/CommissionClient.model'
import {validatePercentage} from '../commissionDefault/core/CommissionDefault.utils'

interface CustomerOption {
  value: number
  label: string
}

const DEFAULT_PARTNERS = ['Passport', 'Conekta', 'Enefevo']

export const ClientCommission: React.FC = () => {
  const intl = useIntl()

  /* ───── estados ───── */
  const [customers,  setCustomers]  = useState<CustomerOption[]>([])
  const [customerId, setCustomerId] = useState<number | null>(null)

  const [data,   setData]   = useState<ClientCommissionItem[]>([])
  const [errors, setErrors] = useState<Record<number, string>>({})

  const [loading, setLoading] = useState<boolean>(true)   // carga de datos / comisiones
  const [saving,  setSaving ] = useState<boolean>(false)  // guardado

  /* ───── 1) cargar lista de clientes ───── */
  useEffect(() => {
    (async () => {
      try {
        const raw = await fetchCustomers()
        setCustomers(raw.map(c => ({value: c.id, label: c.name})))
      } catch {
        Swal.fire('Error', 'No se pudo cargar la lista de clientes', 'error')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  /* ───── 2) cargar comisiones del cliente seleccionado ───── */
  useEffect(() => {
    if (customerId == null) return
    (async () => {
      setLoading(true)
      try {
        const id   = customerId!
        const list = await fetchClientCommissions(id)
        setData(
          list.length
            ? list
            : DEFAULT_PARTNERS.map(tp => ({
                customerId: id,
                techPartner: tp,
                commission : NaN,
                isDefault  : false,
              })),
        )
        setErrors({})
      } catch {
        Swal.fire('Error', 'No se pudo cargar la configuración', 'error')
      } finally {
        setLoading(false)
      }
    })()
  }, [customerId])

  /* ───── edición local ───── */
  const onChange = (idx: number, raw: string) => {
    const v = parseFloat(raw.replace(',', '.'))
    setData(prev => {
      const upd = [...prev]
      upd[idx].commission = v
      upd[idx].isDefault  = false
      return upd
    })

    setErrors(prev =>
      validatePercentage(v)
        ? (() => {
            const {[idx]: _, ...r} = prev
            return r
          })()
        : {...prev, [idx]: '0–100, hasta 2 decimales'},
    )
  }

  const canSave =
    customerId !== null && Object.keys(errors).length === 0 && !saving

  /* ───── guardar ───── */
  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    Swal.fire({title: 'Guardando…', didOpen: () => Swal.showLoading(), allowOutsideClick: false})
    try {
      await saveClientCommissions(
        customerId!,
        data.filter(d => !isNaN(d.commission)),
      )
      Swal.close()
      Swal.fire('¡Listo!', 'Comisión personalizada guardada', 'success')
    } catch {
      Swal.close()
      Swal.fire('Error al guardar', 'Intenta de nuevo', 'error')
    } finally {
      setSaving(false)
    }
  }

  /* ══════════════ render ══════════════ */
  return (
    <div className='card mb-5 mb-xl-10 position-relative'>
      {/* overlay de carga */}
      {loading && (
        <div className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white opacity-75'>
          <span className='spinner-border text-primary mb-3' role='status' />
          <strong>Cargando…</strong>
        </div>
      )}

      <div className='card-body py-3'>
        {/* cabecera */}
        <div className='d-flex flex-wrap align-items-center justify-content-between mb-5'>
          <h3 className='mb-3 mb-md-0'>
            {intl.formatMessage({
              id: 'CLIENT_COMMISSION.TITLE',
              defaultMessage: 'Comisión Personalizada del Cliente',
            })}
          </h3>
          <button
            className='btn btn-primary rounded-pill'
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>

        {/* filtro cliente */}
        <div className='row flex-wrap mb-5'>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <label className='form-label'>Cliente</label>
            <Select<CustomerOption>
              options={customers}
              value={customers.find(c => c.value === customerId) ?? null}
              onChange={opt => setCustomerId(opt?.value ?? null)}
              isDisabled={loading || saving}
              placeholder='Selecciona un cliente…'
              classNamePrefix='react-select'
            />
          </div>
        </div>

        {/* tabla */}
        <div className='table-responsive'>
          <table className='table table-striped'>
            <thead>
              <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                <th>Socio Tecnológico</th>
                <th style={{width: '30%'}}>Comisión (%)</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={row.techPartner}>
                  <td>{row.techPartner}</td>
                  <td>
                    <div className='d-flex align-items-center'>
                      <input
                        type='text'
                        disabled={saving || loading}
                        className={`form-control form-control-solid ${
                          errors[idx] ? 'is-invalid' : ''
                        }`}
                        value={
                          isNaN(row.commission)
                            ? ''
                            : row.commission.toString().replace('.', ',')
                        }
                        onChange={e => onChange(idx, e.target.value)}
                      />
                      <span className='ms-2'>%</span>
                    </div>
                    {errors[idx] && (
                      <div className='invalid-feedback d-block'>
                        {errors[idx]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
